import { FlaskConical } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Unmistakable marker that this is not the real league.
 *
 * Read from `/api/v1/health`, which is unauthenticated, so the banner is present on the
 * sign-in screen too — the one place where mistaking preview for production matters most
 * and where an authenticated-only banner would be missing.
 *
 * It fails *closed in the safe direction*: any error, any unexpected shape, and no banner
 * renders. A banner that appeared spuriously in production would teach everyone to ignore
 * it, which is worse than not having one.
 */

interface HealthPayload {
  data?: { environment?: string; environmentBanner?: string | null };
}

export const EnvironmentBanner = () => {
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    void fetch("/api/v1/health", {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as HealthPayload;
        if (
          payload.data?.environment === "preview" &&
          typeof payload.data.environmentBanner === "string"
        ) {
          setBanner(payload.data.environmentBanner);
        }
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  if (banner === null) {
    return null;
  }

  return (
    <aside className="environment-banner" role="note">
      <FlaskConical aria-hidden size={16} />
      <span>{banner}</span>
    </aside>
  );
};
